import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button, Code, EmptyState, IconVan, ListPane, PageHeader, Section,
  Stamp, Toolbar, Workbench,
} from '~/design-system';
import { useBusiness } from '~/platform/tenancy/BusinessProvider';
import { useRoutes, useShipments } from '~/platform/data/queries';
import { STOP_TONE } from '~/platform/domain/presentation';
import './logistics.css';

/* ==================================================================
   Logistics.

   A route is a strip of stops in load order — which is *reverse*
   delivery order, because the last stop goes in the van first. The
   list is therefore numbered by sequence and the number is the
   primary mark on each stop, the way a loading plan is.
   ================================================================== */

export default function LogisticsPage() {
  const { t } = useTranslation(['logistics', 'common', 'orders']);
  const { business, format } = useBusiness();
  const [date, setDate] = useState(() => format.tradingDay());

  const { data: routes = [], isLoading } = useRoutes(business.id, date);
  const { data: shipments = [] } = useShipments(business.id, date);

  return (
    <Workbench>
      <PageHeader
        eyebrow={t('eyebrow')}
        title={t('title')}
        count={format.date(date, 'weekday')}
        actions={
          <Button variant="primary" icon={<IconVan />}>
            {t('dispatch')}
          </Button>
        }
      />

      <Toolbar>
        <input
          type="date"
          className="pd-input"
          style={{ inlineSize: 170 }}
          value={date}
          onChange={(e) => setDate(e.target.value)}
          aria-label={t('common:labels.date')}
        />
      </Toolbar>

      <ListPane>
        {!isLoading && routes.length === 0 ? (
          <EmptyState title={t('empty')} body={t('emptyBody')} />
        ) : (
          <div className="pd-routes">
            {routes.map((route) => {
              const stops = shipments
                .filter((s) => s.routeId === route.id)
                .sort((a, b) => a.sequence - b.sequence);

              return (
                <article key={route.id} className="pd-route">
                  <header className="pd-route__head">
                    <div className="row-baseline">
                      <h2 className="pd-route__name">{route.name}</h2>
                      <Code>{route.vehicleId ?? '—'}</Code>
                    </div>
                    <div className="row">
                      <span className="stencil">{t('stops', { count: stops.length })}</span>
                      <Stamp
                        tone={route.status === 'dispatched' ? 'warn' : route.status === 'completed' ? 'ok' : 'neutral'}
                        weight={route.status === 'completed' ? 'solid' : 'outline'}
                      >
                        {route.status}
                      </Stamp>
                    </div>
                  </header>

                  {stops.length === 0 ? (
                    <p className="pd-route__empty dim">{t('emptyBody')}</p>
                  ) : (
                    <ol className="pd-route__stops">
                      {stops.map((stop) => (
                        <li key={stop.id} className="pd-stop" data-tone={STOP_TONE[stop.status]}>
                          <span className="pd-stop__seq num">{stop.sequence}</span>
                          <span className="pd-band" aria-hidden="true" style={{ background: 'var(--tone)' }} />

                          <div className="pd-stop__body">
                            <div className="spread">
                              <span className="pd-stop__customer truncate">{stop.customerName}</span>
                              <Stamp tone={STOP_TONE[stop.status]} size="sm">
                                {t(`stopStatus.${stop.status}`)}
                              </Stamp>
                            </div>
                            <p className="pd-stop__address truncate">{stop.addressLabel}</p>
                            <div className="pd-stop__facts">
                              <span>
                                <span className="stencil">{t('common:labels.window')}</span>
                                <span className="num">{format.timeWindow(stop.window)}</span>
                              </span>
                              <span>
                                <span className="stencil">{t('eta')}</span>
                                <span className="num">{format.dateTime(stop.etaAt, 'time')}</span>
                              </span>
                              <span>
                                <span className="stencil">{t('crates')}</span>
                                <span className="num">
                                  {stop.crateCount}
                                  {stop.cratesReturned > 0 ? (
                                    <span className="faint"> / {stop.cratesReturned}</span>
                                  ) : null}
                                </span>
                              </span>
                              <span>
                                <span className="stencil">{t('common:labels.sku')}</span>
                                <Code>{stop.number}</Code>
                              </span>
                            </div>
                            {stop.proof?.signedBy ? (
                              <p className="pd-stop__proof">
                                <span className="stencil">{t('signedBy')}</span>
                                {stop.proof.signedBy}
                                <span className="num faint">
                                  {format.dateTime(stop.proof.capturedAt, 'time')}
                                </span>
                              </p>
                            ) : null}
                          </div>
                        </li>
                      ))}
                    </ol>
                  )}
                </article>
              );
            })}

            {/* Stops with no route yet — the pile on the dock. */}
            <Section title={t('common:labels.none')}>
              <ul className="pd-route__unassigned">
                {shipments
                  .filter((s) => !s.routeId)
                  .map((s) => (
                    <li key={s.id}>
                      <Code>{s.number}</Code>
                      <span className="truncate">{s.customerName}</span>
                      <span className="num faint">{format.timeWindow(s.window)}</span>
                    </li>
                  ))}
              </ul>
            </Section>
          </div>
        )}
      </ListPane>
    </Workbench>
  );
}
